import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  HelpCircle, 
  ChevronRight, 
  ChevronLeft,
  Home,
  Search,
  Package,
  Truck,
  Share2,
  MessageCircle,
  CheckCircle,
  AlertTriangle,
  Clock,
  ExternalLink,
  Copy,
  X
} from 'lucide-react';
import { colors } from '../theme';

const AjudaVendedor = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showAllTopics, setShowAllTopics] = useState(true);

  const tutorialSteps = [
    {
      id: 'bem-vindo',
      title: '👋 Bem-vindo ao Sistema de Rastreamento',
      icon: <Home size={24} />,
      color: 'bg-blue-500',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Este sistema foi criado para você acompanhar seus pedidos de forma simples e rápida!
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">O que você pode fazer:</h4>
            <ul className="space-y-2 text-sm text-blue-700">
              <li className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-500" />
                Ver todos os seus pedidos em um só lugar
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-500" />
                Acompanhar o status de entrega em tempo real
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-500" />
                Enviar link de rastreamento para seus clientes
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-500" />
                Receber alertas de pedidos atrasados
              </li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'dashboard',
      title: '📊 Painel Principal (Dashboard)',
      icon: <Home size={24} />,
      color: 'bg-green-500',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            O Dashboard é sua tela inicial. Ele mostra um resumo rápido dos seus pedidos.
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={16} className="text-red-500" />
                <span className="font-semibold text-red-800 text-sm">Em Atraso</span>
              </div>
              <p className="text-xs text-red-600">Pedidos que já passaram da data de entrega prevista</p>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={16} className="text-yellow-600" />
                <span className="font-semibold text-yellow-800 text-sm">Entrega Hoje</span>
              </div>
              <p className="text-xs text-yellow-600">Pedidos com previsão de entrega para hoje</p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Truck size={16} className="text-blue-500" />
                <span className="font-semibold text-blue-800 text-sm">Em Trânsito</span>
              </div>
              <p className="text-xs text-blue-600">Pedidos que estão a caminho do cliente</p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle size={16} className="text-green-500" />
                <span className="font-semibold text-green-800 text-sm">Entregues</span>
              </div>
              <p className="text-xs text-green-600">Pedidos que já foram entregues com sucesso</p>
            </div>
          </div>
          
          <div className="bg-gray-100 rounded-lg p-3 text-sm">
            <strong>💡 Dica:</strong> Clique em qualquer card para ver a lista detalhada de pedidos daquela categoria!
          </div>
        </div>
      )
    },
    {
      id: 'consultar',
      title: '🔍 Consultando Pedidos',
      icon: <Search size={24} />,
      color: 'bg-purple-500',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Na tela de <strong>"Consultar Pedidos"</strong> você pode buscar e filtrar seus pedidos.
          </p>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-800 mb-3">Como buscar:</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="bg-purple-200 text-purple-800">1</Badge>
                <div>
                  <p className="font-medium text-sm">Use a barra de busca</p>
                  <p className="text-xs text-gray-600">Digite o número do pedido, nome do cliente ou código de rastreio</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge className="bg-purple-200 text-purple-800">2</Badge>
                <div>
                  <p className="font-medium text-sm">Use os filtros</p>
                  <p className="text-xs text-gray-600">Filtre por status (Faturado, Em Trânsito, Entregue) ou transportadora</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge className="bg-purple-200 text-purple-800">3</Badge>
                <div>
                  <p className="font-medium text-sm">Clique no pedido</p>
                  <p className="text-xs text-gray-600">Para ver todos os detalhes e histórico de movimentação</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'rastreamento',
      title: '🚚 Rastreamento de Entregas',
      icon: <Truck size={24} />,
      color: 'bg-orange-500',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Você pode acompanhar o rastreamento em tempo real dos pedidos.
          </p>
          
          <div className="space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <h4 className="font-semibold text-orange-800 text-sm mb-2">📦 Correios</h4>
              <p className="text-xs text-gray-600">
                Pedidos enviados pelos Correios mostram o código de rastreio. 
                Clique no ícone do caminhão para ver os eventos de movimentação.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="font-semibold text-blue-800 text-sm mb-2">🏢 Outras Transportadoras (EDI)</h4>
              <p className="text-xs text-gray-600">
                Pedidos de transportadoras parceiras também são rastreados automaticamente. 
                Os eventos aparecem em "Histórico EDI".
              </p>
            </div>
          </div>
          
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-sm">
            <strong>⚠️ Importante:</strong> O sistema atualiza os rastreamentos automaticamente a cada hora.
          </div>
        </div>
      )
    },
    {
      id: 'compartilhar',
      title: '📤 Enviando Link para o Cliente',
      icon: <Share2 size={24} />,
      color: 'bg-teal-500',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Você pode enviar um link de rastreamento para seu cliente acompanhar a entrega!
          </p>
          
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <h4 className="font-semibold text-teal-800 mb-3">Passo a passo:</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="bg-teal-200 text-teal-800">1</Badge>
                <div>
                  <p className="font-medium text-sm">Encontre o pedido</p>
                  <p className="text-xs text-gray-600">Use a busca ou navegue pela lista</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge className="bg-teal-200 text-teal-800">2</Badge>
                <div>
                  <p className="font-medium text-sm">Clique no ícone de rastreamento</p>
                  <p className="text-xs text-gray-600">O ícone do caminhão 🚚 ou o botão "EDI"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge className="bg-teal-200 text-teal-800">3</Badge>
                <div>
                  <p className="font-medium text-sm">Use os botões de compartilhar</p>
                  <div className="flex gap-2 mt-1">
                    <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      <MessageCircle size={12} /> WhatsApp
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      <Copy size={12} /> Copiar Link
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
            <strong>✅ Dica:</strong> O cliente não precisa fazer login! Ele consegue ver o rastreamento direto pelo link.
          </div>
        </div>
      )
    },
    {
      id: 'historico',
      title: '📋 Histórico do Pedido',
      icon: <Clock size={24} />,
      color: 'bg-indigo-500',
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Cada pedido tem um histórico completo de tudo que aconteceu com ele.
          </p>
          
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-800 mb-3">O que você encontra no histórico:</h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Data e hora de entrada no sistema</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Mudanças de status (Faturado → Em Trânsito → Entregue)</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Quem fez cada alteração e quando</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Observações adicionadas pela equipe</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                <span>Comprovantes de entrega (se houver)</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-gray-100 rounded-lg p-3 text-sm">
            <strong>💡 Como acessar:</strong> Clique no ícone do relógio (🕐) na linha do pedido.
          </div>
        </div>
      )
    },
    {
      id: 'duvidas',
      title: '❓ Dúvidas Frequentes',
      icon: <HelpCircle size={24} />,
      color: 'bg-gray-500',
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="font-semibold text-gray-800 text-sm mb-1">
                Por que não vejo todos os pedidos?
              </h4>
              <p className="text-xs text-gray-600">
                Você só vê os pedidos que estão vinculados ao seu nome de vendedor. 
                Se achar que está faltando algum, fale com o administrador.
              </p>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="font-semibold text-gray-800 text-sm mb-1">
                O rastreamento não está atualizado?
              </h4>
              <p className="text-xs text-gray-600">
                O sistema atualiza automaticamente a cada hora. Se precisar de uma atualização 
                imediata, clique em "Atualizar" na tela de rastreamento.
              </p>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="font-semibold text-gray-800 text-sm mb-1">
                Como sei se um pedido está atrasado?
              </h4>
              <p className="text-xs text-gray-600">
                Pedidos atrasados aparecem em vermelho no Dashboard e na lista de pedidos. 
                Fique de olho no card "Em Atraso"!
              </p>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="font-semibold text-gray-800 text-sm mb-1">
                Posso editar um pedido?
              </h4>
              <p className="text-xs text-gray-600">
                Apenas administradores e lançadores podem editar pedidos. 
                Se precisar de alguma alteração, entre em contato com a equipe.
              </p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      setShowAllTopics(false);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSelectTopic = (index) => {
    setCurrentStep(index);
    setShowAllTopics(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 overflow-hidden">
        {/* Header */}
        <div 
          className="px-6 py-4 text-white"
          style={{ backgroundColor: colors.primaza.primary }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-white text-xl">
              <HelpCircle size={28} />
              Central de Ajuda
            </DialogTitle>
          </DialogHeader>
        </div>

        {showAllTopics ? (
          /* Lista de Tópicos */
          <ScrollArea className="h-[60vh] p-6">
            <div className="space-y-3">
              <p className="text-gray-600 mb-4">
                Selecione um tópico para aprender mais sobre o sistema:
              </p>
              {tutorialSteps.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => handleSelectTopic(index)}
                  className="w-full flex items-center gap-4 p-4 rounded-lg border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all text-left group"
                >
                  <div className={`${step.color} text-white p-3 rounded-lg group-hover:scale-110 transition-transform`}>
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{step.title}</h3>
                  </div>
                  <ChevronRight size={20} className="text-gray-400 group-hover:text-green-500" />
                </button>
              ))}
            </div>
          </ScrollArea>
        ) : (
          /* Conteúdo do Tópico */
          <>
            <ScrollArea className="h-[50vh] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`${tutorialSteps[currentStep].color} text-white p-2 rounded-lg`}>
                  {tutorialSteps[currentStep].icon}
                </div>
                <h2 className="text-lg font-bold text-gray-800">
                  {tutorialSteps[currentStep].title}
                </h2>
              </div>
              {tutorialSteps[currentStep].content}
            </ScrollArea>

            {/* Navegação */}
            <div className="border-t p-4 flex items-center justify-between bg-gray-50">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAllTopics(true)}
              >
                <HelpCircle size={16} className="mr-2" />
                Todos os Tópicos
              </Button>
              
              <div className="flex items-center gap-2">
                {/* Indicadores */}
                <div className="flex gap-1 mr-4">
                  {tutorialSteps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentStep(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentStep 
                          ? 'bg-green-500 w-4' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                >
                  <ChevronLeft size={16} className="mr-1" />
                  Anterior
                </Button>
                <Button
                  size="sm"
                  onClick={handleNext}
                  disabled={currentStep === tutorialSteps.length - 1}
                  style={{ backgroundColor: colors.primaza.primary }}
                  className="text-white"
                >
                  Próximo
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AjudaVendedor;
